// Shared chat-status socket state (ref-counted) for unread indicators

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { chatStatusSocket } from "../../utils/Socket";
import { hasUnreadApplicationChats } from "../utils/applicationChatRoomName";

let unreadMap = {};
const subscribers = new Set();
let listenerCount = 0;
let socketAttached = false;

const notify = () => {
  subscribers.forEach((cb) => cb());
};

const getSnapshot = () => unreadMap;

const subscribe = (cb) => {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};

const onInit = (map) => {
  unreadMap = map || {};
  notify();
};

const onUpdate = ({ roomName, hasUnread, type }) => {
  if (!roomName || type === "reaction") return;
  unreadMap = { ...unreadMap, [roomName]: hasUnread };
  notify();
};

const ensureSocket = () => {
  if (socketAttached) return;
  socketAttached = true;
  chatStatusSocket.connect();
  chatStatusSocket.on("chatStatus:init", onInit);
  chatStatusSocket.on("chatStatus:update", onUpdate);
};

const releaseSocket = () => {
  if (listenerCount > 0) return;
  if (!socketAttached) return;
  socketAttached = false;
  chatStatusSocket.off("chatStatus:init", onInit);
  chatStatusSocket.off("chatStatus:update", onUpdate);
  chatStatusSocket.disconnect();
};

export function useChatStatusUnread({ enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    listenerCount += 1;
    ensureSocket();
    return () => {
      listenerCount -= 1;
      releaseSocket();
    };
  }, [enabled]);

  const map = useSyncExternalStore(
    enabled ? subscribe : () => () => {},
    enabled ? getSnapshot : () => ({}),
    enabled ? getSnapshot : () => ({})
  );

  const setUnreadMap = useCallback((updater) => {
    unreadMap =
      typeof updater === "function" ? updater(unreadMap) : updater || {};
    notify();
  }, []);

  const clearUnreadMap = useCallback(() => {
    unreadMap = {};
    notify();
  }, []);

  const hasApplicationsUnread = enabled && hasUnreadApplicationChats(map);

  return {
    unreadMap: enabled ? map : {},
    hasApplicationsUnread,
    setUnreadMap,
    clearUnreadMap
  };
}
